import React, { useMemo, useState } from "react"
import { createRoot } from "react-dom/client"
import { motion } from "motion/react"
import { playSkillBackSound, playSkillFrontSound } from "@/utils/utils"
import styles from "./index.module.scss"
import { Skill } from "@/components/SkillDialog"

interface SkillAnimationProps {
    skill: Skill
    position?: { row: number; col: number } // 棋子位置
    onComplete?: () => void
}

// 导出异步函数，用于触发技能动画
export const playSkillAnimation = (
    skill: Skill,
    position?: { row: number; col: number }
): Promise<void> => {
    return new Promise((resolve) => {
        // 查找棋盘元素（class 为 grid）
        const gridElement = document.getElementById("grid")
        if (!gridElement) {
            resolve()
            return
        }

        playSkillFrontSound(skill)

        // 创建临时容器
        const container = document.createElement("div")
        // 将容器挂载到棋盘元素上
        gridElement.appendChild(container)
        const root = createRoot(container)

        const handleComplete = () => {
            // 动画完成后清理
            root.unmount()
            if (gridElement.contains(container)) {
                gridElement.removeChild(container)
            }
            resolve()
        }

        // 渲染动画组件
        root.render(
            <SkillAnimation
                skill={skill}
                position={position}
                onComplete={handleComplete}
            />
        )
    })
}

// 动画类型枚举
enum AnimationType {
    FLASH = "flash", // 闪光 - 技能1：击碎四方
    STAR_RAIN = "starRain", // 星辰降临 - 技能2：随机生成棋子
    TELEPORT_VORTEX = "teleportVortex", // 传送漩涡 - 技能3：传送棋子
    CONVERSION_WAVE = "conversionWave", // 转化波纹 - 技能4：转化棋子
    CROSS_FIRE = "crossFire", // 十字火焰 - 技能5：相邻消失
}

// 为每个技能配置专属动画类型
const SKILL_ANIMATIONS: Record<Skill, AnimationType> = {
    [Skill.SKILL1]: AnimationType.FLASH, // 击碎四方 → 十字闪光
    [Skill.SKILL2]: AnimationType.STAR_RAIN, // 随机生成 → 星辰降临
    [Skill.SKILL3]: AnimationType.TELEPORT_VORTEX, // 传送棋子 → 传送漩涡
    [Skill.SKILL4]: AnimationType.CONVERSION_WAVE, // 转化棋子 → 转化波纹
    [Skill.SKILL5]: AnimationType.CROSS_FIRE, // 相邻消失 → 十字火焰
    [Skill.EMPTY]: AnimationType.FLASH, // 默认动画
}

export const SkillAnimation: React.FC<SkillAnimationProps> = ({
    skill,
    position,
    onComplete,
}) => {
    const [showPreAnimation, setShowPreAnimation] = useState(true)

    // 获取技能对应的动画类型
    const animationType = useMemo(() => {
        return SKILL_ANIMATIONS[skill] || null
    }, [skill])

    // 计算动画中心位置（仅对技能1和技能5应用定位）
    const animationStyle = useMemo(() => {
        // 只有技能1（FLASH）和技能5（CROSS_FIRE）需要定位到落子位置
        const needsPositioning =
            animationType === AnimationType.FLASH ||
            animationType === AnimationType.CROSS_FIRE

        if (!needsPositioning || !position) return {}

        // 查找grid容器（父元素）
        const gridElement = document.getElementById("grid")
        if (!gridElement) return {}

        const rect = gridElement.getBoundingClientRect()
        // 9x9棋盘，每个格子的尺寸
        const cellWidth = rect.width / 9
        const cellHeight = rect.height / 9

        // 计算棋子中心位置（相对于grid容器的位置）
        const left = position.col * cellWidth + cellWidth / 2
        const top = position.row * cellHeight + cellHeight / 2

        return {
            left: `${left}px`,
            top: `${top}px`,
            transform: "translate(-50%, -50%)",
        }
    }, [position, animationType])

    // 前置动画完成后，显示特效动画
    const handlePreAnimationComplete = () => {
        setShowPreAnimation(false)
    }

    // 特效动画完成后，整体完成
    const handleEffectComplete = () => {
        playSkillBackSound(skill)
        onComplete?.()
    }

    if (!animationType) return null

    return (
        <div className={styles.skillAnimation}>
            {/* 前置动画：技能图标闪烁 */}
            {showPreAnimation && (
                <SkillIconPreAnimation
                    skill={skill}
                    onComplete={handlePreAnimationComplete}
                />
            )}

            {/* 播放特效动画（技能1和5需要定位到落子位置） */}
            {!showPreAnimation && (
                <div className={styles.effectContainer} style={animationStyle}>
                    {animationType === AnimationType.FLASH && (
                        <FlashAnimation onComplete={handleEffectComplete} />
                    )}
                    {animationType === AnimationType.STAR_RAIN && (
                        <StarRainAnimation onComplete={handleEffectComplete} />
                    )}
                    {animationType === AnimationType.TELEPORT_VORTEX && (
                        <TeleportVortexAnimation
                            onComplete={handleEffectComplete}
                        />
                    )}
                    {animationType === AnimationType.CONVERSION_WAVE && (
                        <ConversionWaveAnimation
                            onComplete={handleEffectComplete}
                        />
                    )}
                    {animationType === AnimationType.CROSS_FIRE && (
                        <CrossFireAnimation onComplete={handleEffectComplete} />
                    )}
                </div>
            )}
        </div>
    )
}

// 前置动画：技能图标闪烁
const SkillIconPreAnimation: React.FC<{
    skill: Skill
    onComplete: () => void
}> = ({ skill, onComplete }) => {
    return (
        <motion.div
            className={styles.skillIconPreAnimation}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{
                scale: [0.8, 2.8],
                opacity: [0, 1],
            }}
            transition={{
                duration: 1,
                ease: "easeOut",
            }}
            onAnimationComplete={onComplete}
        >
            <img
                className={styles.skillIconImage}
                src={require(`@/assets/imgs/skills/${skill}_use.png`)}
            />
        </motion.div>
    )
}

// 1. 闪光动画 - 十字交叉光芒（技能1：击碎四方）
const FlashAnimation: React.FC<{ onComplete: () => void }> = ({
    onComplete,
}) => {
    return (
        <motion.div className={styles.flash}>
            {/* 水平光束 */}
            <motion.div
                className={styles.flashBeamH}
                initial={{ scaleX: 0, opacity: 1 }}
                animate={{ scaleX: 1, opacity: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
            />
            {/* 垂直光束 */}
            <motion.div
                className={styles.flashBeamV}
                initial={{ scaleY: 0, opacity: 1 }}
                animate={{ scaleY: 1, opacity: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                onAnimationComplete={onComplete}
            />
            {/* 中心光点 */}
            <motion.div
                className={styles.flashCenter}
                initial={{ scale: 0, opacity: 1 }}
                animate={{ scale: 2, opacity: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
            />
        </motion.div>
    )
}

// 2. 能量波纹动画 - 5个位置出现能量波纹，从中心向外扩散（技能2：随机生成5个棋子）
const StarRainAnimation: React.FC<{ onComplete: () => void }> = ({
    onComplete,
}) => {
    // 生成5个固定位置的能量波纹（分散在棋盘上）
    const BOARD_SIZE = 9
    const CELL_PERCENT = 100 / BOARD_SIZE

    const spawnPoints = [
        { row: 2, col: 2, label: "topLeft" },
        { row: 2, col: 6, label: "topRight" },
        { row: 4, col: 4, label: "center" },
        { row: 6, col: 2, label: "bottomLeft" },
        { row: 6, col: 6, label: "bottomRight" },
    ]

    return (
        <div className={styles.starRain}>
            {/* 5个位置依次出现能量波纹 */}
            {spawnPoints.map((point, index) => {
                // 参考传送门的定位方式
                const left = (point.col - 0.1) * CELL_PERCENT
                const top = (point.row - 0.1) * CELL_PERCENT

                return (
                    <motion.div
                        key={point.label}
                        className={styles.energyWave}
                        style={{
                            left: `${left}%`,
                            top: `${top}%`,
                        }}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{
                            scale: 1,
                            opacity: 1,
                        }}
                        transition={{
                            duration: 0.3,
                            delay: index * 0.15,
                            ease: "easeOut",
                        }}
                    >
                        {/* 3层能量波纹从中心向外扩散 */}
                        {[0, 1, 2].map((ringIndex) => (
                            <motion.div
                                key={ringIndex}
                                className={styles.energyWaveRing}
                                initial={{ scale: 0, opacity: 1 }}
                                animate={{
                                    scale: 3,
                                    opacity: 0,
                                }}
                                transition={{
                                    duration: 1.2,
                                    delay: index * 0.15 + ringIndex * 0.15,
                                    ease: "easeOut",
                                }}
                                onAnimationComplete={
                                    index === spawnPoints.length - 1 &&
                                    ringIndex === 2
                                        ? onComplete
                                        : undefined
                                }
                            />
                        ))}
                    </motion.div>
                )
            })}
        </div>
    )
}

// 3. 传送门动画 - 5个位置出现传送门然后收缩消失（技能3：传送5枚棋子）
const TeleportVortexAnimation: React.FC<{ onComplete: () => void }> = ({
    onComplete,
}) => {
    // 每个格子占 100/9 ≈ 11.11%，位置是格子中心
    const BOARD_SIZE = 9
    const CELL_PERCENT = 100 / BOARD_SIZE

    const portalPositions = [
        { row: 4, col: 4, label: "center" }, // 中心
        { row: 1, col: 1, label: "top" },
        { row: 1, col: 7, label: "bottom" },
        { row: 7, col: 1, label: "left" },
        { row: 7, col: 7, label: "right" },
    ]

    return (
        <div className={styles.teleportVortex}>
            {/* 5个传送门 */}
            {portalPositions.map((pos, index) => {
                // 计算格子中心位置的百分比
                const left = (pos.col - 0.1) * CELL_PERCENT
                const top = (pos.row - 0.1) * CELL_PERCENT

                return (
                    <motion.div
                        key={pos.label}
                        className={styles.teleportPortal}
                        style={{
                            left: `${left}%`,
                            top: `${top}%`,
                        }}
                        initial={{ scale: 0, rotate: 0, opacity: 0 }}
                        animate={{
                            scale: [0, 1.2, 1, 0],
                            rotate: [0, 180, 360],
                            opacity: [0, 1, 1, 0],
                        }}
                        transition={{
                            duration: 1.5,
                            delay: index * 0.1,
                            ease: "easeInOut",
                        }}
                        onAnimationComplete={
                            index === portalPositions.length - 1
                                ? onComplete
                                : undefined
                        }
                    >
                        {/* 传送门外圈 */}
                        <motion.div
                            className={styles.portalRing}
                            animate={{
                                rotate: 360,
                            }}
                            transition={{
                                duration: 1,
                                delay: index * 0.1 + 0.2,
                                repeat: 1,
                                ease: "linear",
                            }}
                        />
                        {/* 传送门内圈（反向旋转） */}
                        <motion.div
                            className={styles.portalRingInner}
                            animate={{
                                rotate: -360,
                            }}
                            transition={{
                                duration: 0.8,
                                delay: index * 0.1 + 0.2,
                                repeat: 1,
                                ease: "linear",
                            }}
                        />
                        {/* 传送门中心能量 */}
                        <motion.div
                            className={styles.portalCore}
                            animate={{
                                scale: [0, 1, 1, 0],
                                opacity: [0, 1, 1, 0],
                            }}
                            transition={{
                                duration: 1.2,
                                delay: index * 0.1 + 0.1,
                                ease: "easeInOut",
                            }}
                        />
                        {/* 传送粒子效果 */}
                        {[0, 1, 2].map((particleIndex) => {
                            const angle = (particleIndex * Math.PI * 2) / 3
                            return (
                                <motion.div
                                    key={`particle-${particleIndex}`}
                                    className={styles.portalParticle}
                                    initial={{
                                        x: 0,
                                        y: 0,
                                        scale: 1,
                                        opacity: 1,
                                    }}
                                    animate={{
                                        x: [
                                            0,
                                            Math.cos(angle) * 30,
                                            Math.cos(angle) * 50,
                                        ],
                                        y: [
                                            0,
                                            Math.sin(angle) * 30,
                                            Math.sin(angle) * 50,
                                        ],
                                        scale: [1, 0.8, 0],
                                        opacity: [1, 0.8, 0],
                                    }}
                                    transition={{
                                        duration: 1,
                                        delay: index * 0.1 + 0.3,
                                        ease: "easeOut",
                                    }}
                                />
                            )
                        })}
                    </motion.div>
                )
            })}
        </div>
    )
}

// 4. 转化波纹动画 - 扩散的转化能量（技能4：转化5枚棋子）
const ConversionWaveAnimation: React.FC<{ onComplete: () => void }> = ({
    onComplete,
}) => {
    return (
        <div className={styles.conversionWave}>
            {/* 多层转化波纹 */}
            {[0, 1, 2, 3, 4].map((index) => (
                <motion.div
                    key={index}
                    className={styles.conversionWaveRing}
                    initial={{ scale: 0, opacity: 1 }}
                    animate={{
                        scale: 3,
                        opacity: 0,
                    }}
                    transition={{
                        duration: 1.5,
                        delay: index * 0.15,
                        ease: "easeOut",
                    }}
                    onAnimationComplete={index === 4 ? onComplete : undefined}
                />
            ))}

            {/* 转化光点 - 代表5个被转化的棋子 */}
            {[0, 1, 2, 3, 4].map((index) => (
                <motion.div
                    key={`dot-${index}`}
                    className={styles.conversionDot}
                    style={{
                        left: `${50 + Math.cos((index * Math.PI * 2) / 5) * 35}%`,
                        top: `${50 + Math.sin((index * Math.PI * 2) / 5) * 35}%`,
                    }}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{
                        scale: [0, 1.5, 1],
                        opacity: [0, 1, 0],
                    }}
                    transition={{
                        duration: 1,
                        delay: 0.3 + index * 0.1,
                        ease: "easeOut",
                    }}
                />
            ))}
        </div>
    )
}

// 5. 从天而降的火焰动画（技能5：相邻消失）
const CrossFireAnimation: React.FC<{ onComplete: () => void }> = ({
    onComplete,
}) => {
    return (
        <motion.div className={styles.crossFire}>
            {/* 火焰球从天而降 */}
            <motion.div
                className={styles.fallingFireball}
                initial={{ y: -300, scale: 0.5, opacity: 0 }}
                animate={{
                    y: 0,
                    scale: [0.5, 1.2, 1],
                    opacity: [0, 1, 1],
                }}
                transition={{
                    duration: 0.8,
                    ease: "easeIn",
                }}
            >
                {/* 火焰球核心 */}
                <motion.div
                    className={styles.fireballCore}
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [1, 0.8, 1],
                    }}
                    transition={{
                        duration: 0.3,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                />
                {/* 火焰球外层光晕 */}
                <motion.div
                    className={styles.fireballGlow}
                    animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.6, 0.3, 0.6],
                    }}
                    transition={{
                        duration: 0.4,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                />
                {/* 下落时的尾迹 */}
                <motion.div
                    className={styles.fireTrail}
                    initial={{ scaleY: 0, opacity: 0 }}
                    animate={{
                        scaleY: [0, 1, 1],
                        opacity: [0, 0.8, 0],
                    }}
                    transition={{
                        duration: 0.8,
                        ease: "easeOut",
                    }}
                />
            </motion.div>

            {/* 落地爆炸效果 */}
            <motion.div
                className={styles.fireExplosion}
                initial={{ scale: 0, opacity: 0 }}
                animate={{
                    scale: [0, 3, 4],
                    opacity: [0, 1, 0],
                }}
                transition={{
                    duration: 1.2,
                    delay: 0.8,
                    ease: "easeOut",
                }}
                onAnimationComplete={onComplete}
            >
                {/* 爆炸核心闪光 */}
                <motion.div
                    className={styles.explosionCore}
                    initial={{ scale: 0, opacity: 0.8 }}
                    animate={{
                        scale: [0, 2, 0],
                        opacity: [0.8, 0.6, 0],
                    }}
                    transition={{
                        duration: 0.6,
                        delay: 0.8,
                        ease: "easeOut",
                    }}
                />

                {/* 爆炸火花 - 6个 */}
                {[0, 1, 2, 3, 4, 5].map((index) => {
                    const angle = (index * Math.PI * 2) / 6
                    return (
                        <motion.div
                            key={index}
                            className={styles.fireSpark}
                            initial={{ x: 0, y: 0, scale: 0, opacity: 0.8 }}
                            animate={{
                                x: Math.cos(angle) * 120,
                                y: Math.sin(angle) * 120,
                                scale: [0, 1.2, 0],
                                opacity: [0.8, 0.6, 0],
                            }}
                            transition={{
                                duration: 1,
                                delay: 0.8 + index * 0.03,
                                ease: "easeOut",
                            }}
                        />
                    )
                })}
            </motion.div>
        </motion.div>
    )
}
